import { useEffect, useState, useCallback } from "react";
import { Search, Filter, RefreshCw, GraduationCap, LogIn, LogOut, AlertTriangle, Clock, ImageIcon, X, ShieldAlert, ChevronLeft, ChevronRight } from "lucide-react";
import { LoadMoreBar, PAGE_SIZE as SERVER_PAGE, totalFrom } from "@/components/LoadMoreBar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { displayName } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ExcuseRequestsCard } from "@/components/ExcuseRequestsCard";

const eventLabels = {
  drop_off: { label: "Drop off", Icon: LogIn, tone: "emerald" },
  pickup: { label: "Pickup", Icon: LogOut, tone: "blue" },
  tardy: { label: "Tardy", Icon: Clock, tone: "amber" },
  early_dismissal: { label: "Early dismissal", Icon: AlertTriangle, tone: "orange" },
};

const PAGE_SIZE = 50;

export default function AttendanceLogPage() {
  const [filters, setFilters] = useState({ q: "", event_type: "", from: "", to: "" });
  const [rows, setRows] = useState([]);
  const [rejections, setRejections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [photoModal, setPhotoModal] = useState({ open: false, src: "", title: "" });
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [moreLoading, setMoreLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        q: filters.q || undefined,
        event_type: filters.event_type || undefined,
        from: filters.from ? new Date(filters.from + "T00:00:00Z").toISOString() : undefined,
        to: filters.to ? new Date(filters.to + "T23:59:59Z").toISOString() : undefined,
        limit: SERVER_PAGE,
      };
      const [ev, rej] = await Promise.all([
        api.get("/student-events", { params }),
        api.get("/pickup-rejections", { params: { q: params.q, from: params.from, to: params.to, limit: 200 } }),
      ]);
      setRows(ev.data); setTotal(totalFrom(ev));
      setRejections(rej.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => { setPage(0); setFilters({ ...filters, [k]: e.target.value }); };

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = rows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const pageStart = rows.length === 0 ? 0 : safePage * PAGE_SIZE + 1;
  const pageEnd = safePage * PAGE_SIZE + pageRows.length;

  return (
    <div className="p-6 md:p-10" data-testid="attendance-log-page">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500">School</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Attendance log</h1>
        </div>
        <button onClick={load} data-testid="attendance-refresh-btn" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <section className="rounded-xl border border-slate-200 bg-white p-4 mb-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-3"><Filter className="h-3.5 w-3.5" /> Filters</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={filters.q} onChange={set("q")} data-testid="attendance-search" placeholder="Search student or parent…" className="w-full h-10 rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
          </div>
          <select value={filters.event_type} onChange={set("event_type")} data-testid="attendance-type" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none">
            <option value="">All events</option>
            <option value="drop_off">Drop off</option>
            <option value="pickup">Pickup</option>
            <option value="tardy">Tardy</option>
            <option value="early_dismissal">Early dismissal</option>
          </select>
          <input type="date" value={filters.from} onChange={set("from")} data-testid="attendance-from" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
          <input type="date" value={filters.to} onChange={set("to")} data-testid="attendance-to" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
        </div>
      </section>

      <ExcuseRequestsCard />

      {rejections.length > 0 && (
        <section className="rounded-xl border border-rose-200 bg-rose-50/50 overflow-hidden mb-6" data-testid="pickup-rejections-card">
          <div className="px-5 py-4 border-b border-rose-200 flex items-center justify-between bg-rose-50">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-rose-600" />
              <span className="font-display font-semibold text-rose-900">Rejected pickup attempts</span>
            </div>
            <span className="inline-flex rounded-full bg-rose-600 px-2.5 py-0.5 text-[11px] font-semibold text-white" data-testid="pickup-rejections-count">
              {rejections.length}
            </span>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pickup-rejections-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-rose-700/70 bg-rose-50/60">
                <th className="px-5 py-2.5">When</th>
                <th className="px-5 py-2.5">Student</th>
                <th className="px-5 py-2.5">Attempted by (scanned)</th>
                <th className="px-5 py-2.5">DL / ID</th>
                <th className="px-5 py-2.5">ID Photo</th>
                <th className="px-5 py-2.5">Officer on duty</th>
                <th className="px-5 py-2.5">Reason</th>
              </tr>
            </thead>
            <tbody>
              {rejections.map((r) => (
                <tr key={r.id} className="border-t border-rose-100 bg-white hover:bg-rose-50/40" data-testid={`rejection-row-${r.id}`}>
                  <td className="px-5 py-2.5 text-xs text-slate-600 whitespace-nowrap">{new Date(r.occurred_at).toLocaleString()}</td>
                  <td className="px-5 py-2.5">
                    <div className="font-medium text-slate-900">{r.student_name || "—"}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{r.event_type === "early_dismissal" ? "Early dismissal" : "Pickup"}</div>
                  </td>
                  <td className="px-5 py-2.5 text-slate-800 font-medium">{r.picker_name || "—"}</td>
                  <td className="px-5 py-2.5 font-mono text-xs text-slate-700">{r.picker_license || "—"}</td>
                  <td className="px-5 py-2.5">
                    {r.picker_photo ? (
                      <button
                        type="button"
                        onClick={() => setPhotoModal({ open: true, src: r.picker_photo, title: `Rejected attempt · ${r.picker_name || "unknown"} ${r.picker_license ? `· ${r.picker_license}` : ""}` })}
                        data-testid={`rejection-photo-${r.id}`}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-lg overflow-hidden border border-rose-200 bg-slate-50 hover:ring-2 hover:ring-rose-400/50 transition"
                        title="View captured ID photo"
                      >
                        <img src={r.picker_photo} alt="ID" className="h-full w-full object-cover" />
                      </button>
                    ) : (
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-dashed border-slate-200 text-slate-300">
                        <ImageIcon className="h-4 w-4" />
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-slate-700">{displayName(r.officer_name) || "—"}</td>
                  <td className="px-5 py-2.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-800">
                      <X className="h-3 w-3" /> {r.reason === "student_not_found" ? "Student not on roster" : "ID not authorized"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </section>
      )}

      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="attendance-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="px-5 py-3">Student</th>
              <th className="px-5 py-3">Event</th>
              <th className="px-5 py-3">Picker</th>
              <th className="px-5 py-3">DL / ID</th>
              <th className="px-5 py-3">ID Photo</th>
              <th className="px-5 py-3">Officer</th>
              <th className="px-5 py-3">When</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (<tr><td colSpan={7} className="px-5 py-14 text-center text-slate-500">No events match the filters.</td></tr>)}
            {pageRows.map((r) => {
              const cfg = eventLabels[r.event_type] || { label: r.event_type, Icon: GraduationCap, tone: "slate" };
              return (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`attendance-row-${r.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium text-slate-900">{r.student_name}</div>
                    {r.student_grade && <div className="text-xs text-slate-500">{r.student_grade}</div>}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
                      cfg.tone === "emerald" ? "bg-emerald-100 text-emerald-800"
                      : cfg.tone === "blue" ? "bg-blue-100 text-blue-800"
                      : cfg.tone === "amber" ? "bg-amber-100 text-amber-800"
                      : cfg.tone === "orange" ? "bg-orange-100 text-orange-800"
                      : "bg-slate-100 text-slate-700"}`}>
                      <cfg.Icon className="h-3 w-3" /> {cfg.label}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-700">{r.picker_name || "—"}</td>
                  <td className="px-5 py-3 text-slate-700 font-mono text-xs">{r.picker_license || "—"}</td>
                  <td className="px-5 py-3">
                    {r.picker_photo ? (
                      <button
                        type="button"
                        onClick={() => setPhotoModal({ open: true, src: r.picker_photo, title: `${r.picker_name || "ID photo"} · ${r.picker_license || ""}` })}
                        data-testid={`attendance-photo-${r.id}`}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-lg overflow-hidden border border-slate-200 bg-slate-50 hover:ring-2 hover:ring-primary/40 transition"
                        title="View captured ID photo"
                      >
                        <img src={r.picker_photo} alt="ID" className="h-full w-full object-cover" />
                      </button>
                    ) : (
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-dashed border-slate-200 text-slate-300">
                        <ImageIcon className="h-4 w-4" />
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-700">{displayName(r.officer_name) || "—"}</td>
                  <td className="px-5 py-3 text-slate-700 whitespace-nowrap text-xs">{new Date(r.occurred_at).toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-600">
          <div data-testid="attendance-pagination-info">
            Showing <b>{pageStart}</b>–<b>{pageEnd}</b> of <b>{rows.length}</b>{total > rows.length && <> loaded · <b>{total.toLocaleString()}</b> total</>}
            {total > rows.length && (
              <button disabled={moreLoading} data-testid="attendance-load-more" className="ml-3 inline-flex items-center gap-1 rounded-md border border-primary/30 bg-white px-2.5 py-1 text-primary font-semibold hover:bg-primary/5 disabled:opacity-50"
                onClick={async () => {
                  setMoreLoading(true);
                  try {
                    const res = await api.get("/student-events", { params: { q: filters.q || undefined, event_type: filters.event_type || undefined, from: filters.from ? new Date(filters.from + "T00:00:00Z").toISOString() : undefined, to: filters.to ? new Date(filters.to + "T23:59:59Z").toISOString() : undefined, limit: SERVER_PAGE, offset: rows.length } });
                    setRows((prev) => [...prev, ...res.data]); setTotal(totalFrom(res));
                  } catch { toast.error("Failed to load more"); } finally { setMoreLoading(false); }
                }}>Load {Math.min(SERVER_PAGE, total - rows.length)} more</button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(Math.max(0, safePage - 1))}
              disabled={safePage === 0}
              data-testid="attendance-prev-page"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-100 disabled:opacity-40 transition"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <button
              onClick={() => setPage(safePage + 1)}
              disabled={safePage >= totalPages - 1}
              data-testid="attendance-next-page"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-100 disabled:opacity-40 transition"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </section>

      <Dialog open={photoModal.open} onOpenChange={(o) => !o && setPhotoModal({ open: false, src: "", title: "" })}>
        <DialogContent className="sm:max-w-lg" data-testid="attendance-photo-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 pr-6">
              <ImageIcon className="h-5 w-5 text-primary" /> Captured ID photo
            </DialogTitle>
            <DialogDescription>{photoModal.title || "Driver's license photo captured at the kiosk."}</DialogDescription>
          </DialogHeader>
          {photoModal.src && (
            <img src={photoModal.src} alt="Driver's license" className="w-full rounded-lg border border-slate-200" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
